import ThemeToggle from '../components/ThemeToggle'
import SectionIndicator from './SectionIndicator'
import { BRAND_COLOURS, COMPETITORS, PERSONAS, PRICING_MODELS, STACK, TONE } from './data'

import personaMark from '../assets/caseStudy/persona-mark.webp'
import personaWendy from '../assets/caseStudy/persona-wendy.webp'
import personaDaniel from '../assets/caseStudy/persona-daniel.webp'
import personaFiona from '../assets/caseStudy/persona-fiona.webp'
import moodboard from '../assets/caseStudy/moodboard.webp'
import typeHeading from '../assets/caseStudy/type-heading.webp'
import typeBody from '../assets/caseStudy/type-body.webp'
import logoTaglineGold from '../assets/caseStudy/logo-tagline-gold.svg'
import logoIconGold from '../assets/caseStudy/logo-icon-gold.svg'
import logoIconSilver from '../assets/caseStudy/logo-icon-silver.svg'
import brandOldLogo from '../assets/caseStudy/brand-old-logo.webp'
import brandGuide from '../assets/caseStudy/brand-guide.webp'
import brandGuideScalable from '../assets/caseStudy/brand-guide-scalable.webp'
import brandGuideErrors from '../assets/caseStudy/brand-guide-errors.webp'
import brandCard from '../assets/caseStudy/brand-card.webp'
import brandApparel from '../assets/caseStudy/brand-apparel.webp'
import brandSupplements from '../assets/caseStudy/brand-supplements.webp'
import designCanvas from '../assets/caseStudy/design-canvas.webp'
import designPricing from '../assets/caseStudy/design-pricing.webp'
import designModal from '../assets/caseStudy/design-modal.webp'
import productHome from '../assets/caseStudy/product-home.webp'
import productPricing from '../assets/caseStudy/product-pricing.webp'
import productAbout from '../assets/caseStudy/product-about.webp'
import productApproach from '../assets/caseStudy/product-approach.webp'
import productCoaching from '../assets/caseStudy/product-coaching.webp'
import productMobileHome from '../assets/caseStudy/product-mobile-home.webp'
import productMobilePricing from '../assets/caseStudy/product-mobile-pricing.webp'
import emailShot from '../assets/caseStudy/email.webp'

import './PowerByJS.scss'

const PERSONA_IMAGES = [personaMark, personaWendy, personaDaniel, personaFiona]

const SITE = 'https://powerbyjs.com'

/**
 * The PowerByJS case study.
 *
 * A separate Vite entry point rather than a route. The home page is a single
 * scrolling document with no router, and adding one so that a second page could
 * exist would put routing weight into a bundle that most visitors never need.
 * Netlify serves a real file in preference to the SPA rewrite, so `/powerbyjs`
 * resolves here and the home page ships byte for byte unchanged.
 *
 * The copy lives in the repository rather than in Sanity on purpose. This is a
 * designed argument rather than a list of content: every section has its own
 * layout, and a portable text schema flexible enough to express them would be
 * more machinery than the single document it serves.
 */
const PowerByJS = () => (
  <>
    <div className='cs-topbar'>
      <div className='cs-topbar-left'>
        <a className='cs-back' href='/'>
          <span aria-hidden='true'>&larr;</span>
          <span className='cs-back-label'>Miguel Vilhena</span>
        </a>
        <SectionIndicator />
      </div>
      <ThemeToggle />
    </div>

    <main className='cs'>
      <header className='cs-hero'>
        <p className='cs-eyebrow'>Case study, freelance</p>
        <h1>
          He asked for one page.
          <br />
          <span>I shipped him a business.</span>
        </h1>
        <p className='cs-standfirst'>
          Jessy Schneider coaches kickboxing and holistic health in Haarlem. He came to me for a one
          page website. I researched the market he was competing in first, found that one page would
          lose, and built what the business actually needed: a brand, eight bilingual pages, and
          three pricing calculators wired straight into his inbox.
        </p>

        <dl className='cs-meta'>
          <div>
            <dt>Client</dt>
            <dd>PowerByJS, Haarlem</dd>
          </div>
          <div>
            <dt>Role</dt>
            <dd>Research, brand, design, build</dd>
          </div>
          <div>
            <dt>Team</dt>
            <dd>Me</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              <span className='cs-live' /> Live and in use
            </dd>
          </div>
        </dl>

        <img
          className='cs-hero-shot'
          src={productHome}
          alt='The PowerByJS home page'
          width={1440}
          height={900}
        />
      </header>

      {/* 01 -------------------------------------------------------------- */}
      <section className='cs-section'>
        <p className='cs-num'>01</p>
        <h2>The brief</h2>

        <blockquote className='cs-quote'>
          <p>
            &ldquo;CMS, to be able to update website autonomously in the future. For now we want
            only 1 website.&rdquo;
          </p>
          <footer>Jessy, in the agreement we signed</footer>
        </blockquote>

        <p>
          He had a logo he did not like, no copy, no brand and no site. New clients reached him
          through Instagram direct messages, which meant every enquiry started with him explaining
          what he offered and what it cost, one message at a time.
        </p>
        <p>
          What he asked for was one page and a way to edit it himself. The straightforward job was
          to build exactly that. I did the research first anyway, because a page is only worth
          building once you know what it has to beat.
        </p>
      </section>

      {/* 02 -------------------------------------------------------------- */}
      <section className='cs-section'>
        <p className='cs-num'>02</p>
        <h2>Why one page would have lost</h2>
        <p className='cs-lede'>
          I studied the personal training market around Haarlem and Amsterdam before drawing
          anything. Three competitors set the standard his prospects were unconsciously measuring
          him against.
        </p>

        <ul className='cs-competitors'>
          {COMPETITORS.map((c) => (
            <li key={c.name}>
              <p className='cs-competitor-sells'>Sells on {c.sells.toLowerCase()}</p>
              <h3>{c.name}</h3>
              <p className='cs-competitor-place'>{c.place}</p>
              <p>{c.detail}</p>
            </li>
          ))}
        </ul>

        <p>
          Jessy charges premium rates and earns them. Against those three, though, a single page
          with a contact form does not read as the cheaper option, it reads as the less serious one.
          The gap was never design taste. It was credibility, and credibility is cumulative: it
          comes from being able to answer the questions a careful buyer asks before they will spend
          a four figure sum on a stranger.
        </p>
        <p className='cs-callout'>
          So I told him the brief was wrong, and why. He agreed to the larger scope on the
          understanding that I would show the reasoning at every step rather than disappear and come
          back with opinions.
        </p>
      </section>

      {/* 03 -------------------------------------------------------------- */}
      <section className='cs-section'>
        <p className='cs-num'>03</p>
        <h2>Who we were selling to</h2>
        <p className='cs-lede'>
          Four personas, drawn from his actual client base rather than invented. Each carries
          demographics, goals, obstacles, engagement preferences and how they map onto his coaching
          pillars.
        </p>

        <div className='cs-personas'>
          {PERSONAS.map((p, i) => (
            <figure key={p.name}>
              <img
                src={PERSONA_IMAGES[i]}
                alt={`Persona card for ${p.name}`}
                width={1200}
                height={692}
                loading='lazy'
              />
              <figcaption>
                <strong>{p.name}</strong> {p.note}
              </figcaption>
            </figure>
          ))}
        </div>

        <p>
          These were not decoration for a slide deck. Daniel and Fiona are time poor and buy
          expertise, so pricing had to be legible without a phone call. Mark needs structure, which
          is why the twelve week programmes are sold as programmes rather than as sessions. Wendy
          does not identify as an athlete, so the copy never assumes she does. The personas decided
          the page structure and the tone before a single screen was designed.
        </p>
      </section>

      {/* 04 -------------------------------------------------------------- */}
      <section className='cs-section'>
        <p className='cs-num'>04</p>
        <h2>The brand</h2>
        <p className='cs-lede'>
          He arrived with a logo he did not like. He left with an identity, and with a document
          that lets the next person he hires apply it without asking me first.
        </p>

        <div className='cs-logos cs-logos--compare'>
          <figure>
            <img
              src={brandOldLogo}
              alt='The previous PowerByJS logo, a thin gold script wordmark'
              width={1280}
              height={292}
              loading='lazy'
            />
            <figcaption>Before</figcaption>
          </figure>
          <figure>
            <img
              src={logoTaglineGold}
              alt='The new PowerByJS logo, gold finish'
              width={270}
              height={152}
              loading='lazy'
            />
            <figcaption>After</figcaption>
          </figure>
        </div>
        <p className='cs-caption'>
          The old mark was a hairline script. At a glance it reads as a signature rather than as a
          strength coach, and the strokes are thin enough that it closes up in a favicon and
          disappears entirely once anyone tries to print it. A logo for this business has to survive
          being embroidered on a hoodie in a badly lit gym. That was the argument, and it is easier
          to see than to make.
        </p>

        <div className='cs-logos'>
          <img
            src={logoIconGold}
            alt='The PowerByJS monogram, gold finish'
            width={270}
            height={152}
            loading='lazy'
          />
          <img
            src={logoIconSilver}
            alt='The PowerByJS monogram, silver finish'
            width={270}
            height={152}
            loading='lazy'
          />
        </div>
        <p className='cs-caption'>
          Twelve lockups in total: icon, wordmark and tagline versions, each in gold and silver,
          each tuned for the two background blacks. The two finishes are not decoration, the site
          can shift between them.
        </p>

        <h3 className='cs-sub'>Palette</h3>
        <ul className='cs-swatches'>
          {BRAND_COLOURS.map((c) => (
            <li key={c.hex}>
              <span className='cs-swatch' style={{ background: c.hex }} aria-hidden='true' />
              <strong>{c.name}</strong>
              <code>{c.hex}</code>
              <span className='cs-swatch-role'>{c.role}</span>
            </li>
          ))}
        </ul>
        <div className='cs-gradients'>
          <div className='cs-gradient cs-gradient--gold'>Radial gold, Flax to Golden Brown</div>
          <div className='cs-gradient cs-gradient--silver'>
            Radial silver, Platinum to Battleship Gray
          </div>
        </div>

        <h3 className='cs-sub'>Typography</h3>
        <div className='cs-type'>
          <img
            src={typeHeading}
            alt='Archivo Black type specimen'
            width={1200}
            height={675}
            loading='lazy'
          />
          <img
            src={typeBody}
            alt='Montserrat type specimen'
            width={1200}
            height={675}
            loading='lazy'
          />
        </div>

        <h3 className='cs-sub'>Tone of voice</h3>
        <ul className='cs-tone'>
          {TONE.map((t) => (
            <li key={t.word}>
              <strong>{t.word}</strong>
              <span>{t.detail}</span>
            </li>
          ))}
        </ul>

        <p className='cs-tagline'>Lead Your Transformation</p>
        <p className='cs-caption'>
          The tagline had to work as a promise to the client and as an instruction to Jessy himself.
          It ships on the hero and in the logo lockups, and he had it printed on shirts that he wears
          around the gym and hands to clients. That is the honest test of an identity: the client
          starts spending his own money extending it.
        </p>

        <h3 className='cs-sub'>Off the screen</h3>
        <p>
          The guide did not stop at the website, because the brand was never only going to live
          there. It specifies the identity on print, on apparel and on packaging, so Jessy can order
          any of it without coming back to ask what the gold is.
        </p>
        <div className='cs-applied'>
          <img
            src={brandCard}
            alt='The PowerByJS business card in gold on black'
            width={1200}
            height={900}
            loading='lazy'
          />
          <img
            src={brandApparel}
            alt='The PowerByJS logo on a black t-shirt'
            width={1200}
            height={900}
            loading='lazy'
          />
          <img
            src={brandSupplements}
            alt='The PowerByJS identity applied to supplement packaging'
            width={1200}
            height={829}
            loading='lazy'
          />
        </div>
        <p className='cs-caption'>
          Mockups from the guide rather than photographs. The apparel is the part that left the
          page: he had the shirts made and they are in the gym.
        </p>

        <h3 className='cs-sub'>Written down, in both languages</h3>
        <p>
          All of it is a versioned editorial document rather than a folder of exported files,
          because Jessy will hire other people. A photographer, a printer, someone running his ads,
          whoever builds the next thing. Fifty two pages, in English and in Dutch, opening with what
          the brand is for before it gets anywhere near a rule, then the lockups, the clear space,
          the approved pairings and the file types. He can hand it over and get work back that still
          looks like him.
        </p>
        <img
          className='cs-wide'
          src={brandGuide}
          alt='The pages of the PowerByJS brand style guide'
          width={2118}
          height={1827}
          loading='lazy'
        />
        <p className='cs-caption'>
          Shown without the two closing spreads, which carry his contact details.
        </p>

        <div className='cs-compare'>
          <figure>
            <img
              src={brandGuideScalable}
              alt='A page explaining which logo lockup to use in which space'
              width={1400}
              height={788}
              loading='lazy'
            />
            <figcaption>Choosing a lockup for the space you have</figcaption>
          </figure>
          <figure>
            <img
              src={brandGuideErrors}
              alt='A page showing common typographic mistakes to avoid'
              width={1400}
              height={788}
              loading='lazy'
            />
            <figcaption>And the ways it goes wrong</figcaption>
          </figure>
        </div>
        <p className='cs-caption'>
          A guide that only shows a brand at its best is decoration. The half that does the work is
          the half telling the next person what not to do, which is the half that survives being
          read by someone in a hurry.
        </p>
      </section>

      {/* 05 -------------------------------------------------------------- */}
      <section className='cs-section'>
        <p className='cs-num'>05</p>
        <h2>From moodboard to product</h2>

        <img
          className='cs-wide'
          src={moodboard}
          alt='The PowerByJS design moodboard'
          width={1600}
          height={820}
          loading='lazy'
        />
        <p className='cs-caption'>
          The moodboard set the register before any layout existed: dark, restrained, premium,
          closer to a watch brand than to a gym. Sketches and low fidelity wireframes came next,
          then high fidelity screens in Figma, then the build.
        </p>

        <img
          className='cs-wide'
          src={designCanvas}
          alt='The Figma canvas showing all eight pages designed end to end'
          width={2105}
          height={2385}
          loading='lazy'
        />
        <p className='cs-caption'>
          Every page was drawn end to end at full fidelity before any of it was built, in both
          languages and in each interaction state. The questions that normally get answered in code,
          halfway through building, were settled here while they were still cheap to change.
        </p>

        <div className='cs-grid-2'>
          <figure>
            <img
              src={productAbout}
              alt='The About page'
              width={1200}
              height={750}
              loading='lazy'
            />
            <figcaption>About, where the credibility case is made</figcaption>
          </figure>
          <figure>
            <img
              src={productApproach}
              alt='The Our Approach page'
              width={1200}
              height={750}
              loading='lazy'
            />
            <figcaption>Our Approach, the method spelled out</figcaption>
          </figure>
          <figure>
            <img
              src={productCoaching}
              alt='The Coaching service page'
              width={1200}
              height={750}
              loading='lazy'
            />
            <figcaption>One of four service pages</figcaption>
          </figure>
        </div>

        <figure className='cs-phone-figure'>
          <img
            src={productMobileHome}
            alt='The home page on a phone'
            width={500}
            height={820}
            loading='lazy'
          />
          <figcaption>Most of his traffic arrives from Instagram, so mobile led the design</figcaption>
        </figure>

        <ul className='cs-stats'>
          <li>
            <strong>8</strong> pages
          </li>
          <li>
            <strong>33</strong> components
          </li>
          <li>
            <strong>2</strong> languages
          </li>
          <li>
            <strong>294</strong> strings each
          </li>
        </ul>
        <p className='cs-caption'>
          English and Dutch across eleven namespaces, at exact parity. Nothing falls back to English
          silently, which matters because half his market would read a missing string as
          carelessness.
        </p>

        <ul className='cs-stack'>
          {STACK.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      </section>

      {/* 06 -------------------------------------------------------------- */}
      <section className='cs-section'>
        <p className='cs-num'>06</p>
        <h2>Three pricing engines, not one</h2>
        <p className='cs-lede'>
          The single most useful thing on the site. His services do not share a pricing shape, so
          they do not share a calculator. Each card encodes the real commercial model he already
          sold by hand.
        </p>

        <div className='cs-compare'>
          <figure>
            <img
              src={designPricing}
              alt='The pricing page drawn in Figma'
              width={1366}
              height={816}
              loading='lazy'
            />
            <figcaption>Drawn in Figma</figcaption>
          </figure>
          <figure>
            <img
              src={productPricing}
              alt='The three pricing calculators as they shipped'
              width={1440}
              height={860}
              loading='lazy'
            />
            <figcaption>Shipped</figcaption>
          </figure>
        </div>
        <p className='cs-caption'>
          The calculators were specified in Figma down to the selected states, so the build was a
          matter of encoding arithmetic that had already been agreed rather than inventing a pricing
          model at the keyboard.
        </p>

        <ul className='cs-pricing'>
          {PRICING_MODELS.map((m) => (
            <li key={m.name}>
              <h3>{m.name}</h3>
              <p className='cs-rate'>
                <strong>{m.rate}</strong> {m.unit}
              </p>
              <code>{m.formula}</code>
              <p>{m.detail}</p>
            </li>
          ))}
        </ul>

        <p>
          The discount logic is the part I care about. Jessy&apos;s offer of twenty two sessions for
          the price of twenty existed only in his head and in conversations. Encoding it means the
          site never quotes a number he would not honour, and a visitor meets the deal at the moment
          they are weighing the larger commitment rather than after they have already talked
          themselves down to five sessions.
        </p>
        <img
          className='cs-phone'
          src={productMobilePricing}
          alt='The pricing calculator on a phone'
          width={500}
          height={1000}
          loading='lazy'
        />
      </section>

      {/* 07 -------------------------------------------------------------- */}
      <section className='cs-section'>
        <p className='cs-num'>07</p>
        <h2>What lands in his inbox</h2>
        <p className='cs-lede'>
          A calculator that forgets its own answer the moment someone clicks Contact is a toy.
          Whatever the visitor configured travels into the enquiry.
        </p>

        <img
          className='cs-wide'
          src={designModal}
          alt='The enquiry modal, showing the estimated price above the form'
          width={1366}
          height={760}
          loading='lazy'
        />
        <p className='cs-caption'>
          The enquiry modal opens over the calculator and carries the estimate with it, so nobody
          has to retype what they just chose. What it shows the visitor is what it sends.
        </p>

        <img
          className='cs-email'
          src={emailShot}
          alt='The branded email Jessy receives when someone enquires'
          width={760}
          height={940}
          loading='lazy'
        />

        <p>
          The form posts to a Netlify function which sends through Resend, with the visitor&apos;s
          address set as the reply-to so Jessy can answer from his inbox. The email itself is hand
          built table markup in the brand palette, because mail clients each render modern CSS
          differently and a broken layout is what most contact form emails actually look like.
        </p>
        <p>
          The effect is small and it matters: he opens a message that already tells him the plan,
          the number of sessions, the group size, the estimated price and which language they were
          reading in. His first reply can be about scheduling instead of about pricing.
        </p>
      </section>

      {/* 08 -------------------------------------------------------------- */}
      <section className='cs-section'>
        <p className='cs-num'>08</p>
        <h2>What is in the build</h2>
        <p>
          Before quoting I ran a twenty one question intake covering the business, the market, and
          how customers actually find him. The competitor row came back as not known yet, which is
          the right answer from a man who spends his days coaching rather than studying the market,
          and it is why the research further up this page is mine and not his. He came in wanting a
          CMS and one page. The research came back with four audiences, two languages and three ways
          to buy. I wrote the spec for that, and then I built it.
        </p>
        <p>Thirty three components across eight routes. The parts worth naming:</p>

        <div className='cs-inventory'>
          <div>
            <h3>Interface</h3>
            <ul>
              <li>
                <strong>Scroll-linked feature reveals.</strong> Each slide owns a viewport height of
                track, with position and opacity driven off scroll progress, so his method is
                demonstrated as you move through it rather than asserted in a paragraph. That is
                what Dedicated Daniel came to evaluate.
              </li>
              <li>
                <strong>A service overlay that stays hidden until hover.</strong> All three tiers
                sit one gesture away from every page without spending a navigation bar, or a
                scroll, on them. It only mounts on pointer devices, so a phone is never offered an
                affordance it cannot trigger.
              </li>
              <li>
                <strong>Before and after cards, headed with weeks and age.</strong> Wellness Wendy
                needs to see someone like her, on her timescale, rather than an athlete.
              </li>
              <li>
                <strong>A testimonial carousel</strong> with autoplay and wheel gestures, linking
                out to the originals rather than reprinting them.
              </li>
              <li>
                <strong>An FAQ accordion built on Headless UI disclosures</strong>, keyboard and
                screen reader correct without hand rolled ARIA. It answers the price and commitment
                questions that people will not send an email to ask.
              </li>
            </ul>
          </div>

          <div>
            <h3>Content</h3>
            <ul>
              <li>
                <strong>Every section backed by Sanity</strong>, so the copy, the prices, the
                testimonials and the transformation photographs are all his to change.
              </li>
              <li>
                <strong>English and Dutch in full.</strong> 294 strings at exact parity across
                eleven namespaces, pluralised properly rather than concatenated, so a Dutch reader
                never lands on an English fragment.
              </li>
              <li>
                <strong>Transformation photographs served through Sanity&apos;s image pipeline</strong>
                , with format negotiation on and cropping off. A before and after photograph that
                the CDN has decided to crop is no longer evidence of anything.
              </li>
            </ul>
          </div>

          <div>
            <h3>Conversion</h3>
            <ul>
              <li>
                <strong>Three pricing engines</strong>, one per service, each with its own
                arithmetic.
              </li>
              <li>
                <strong>A contact modal reachable through context from any component</strong>, so
                every call to action on the site lands in the same place and arrives carrying what
                the visitor had configured.
              </li>
              <li>
                <strong>Delivery through a Netlify function</strong> with reply-to set to the
                visitor, so he answers by hitting reply.
              </li>
            </ul>
          </div>
        </div>

        <p className='cs-inventory-tail'>
          Then the half nobody notices until it is missing: scroll position reset on every route
          change, a real 404 rather than a blank screen, and the services typed such that adding a
          fourth one without its copy fails the build instead of rendering an empty panel in front
          of a customer.
        </p>
      </section>

      {/* 09 -------------------------------------------------------------- */}
      <section className='cs-section'>
        <p className='cs-num'>09</p>
        <h2>Handing it over</h2>
        <p>
          The one thing he asked for by name was the ability to run it himself, so that is the part
          I refused to compromise. Content he needs to change sits in Sanity. He got written
          documentation covering the accounts, the logins and how to edit each type of content,
          written for someone who does not work in software.
        </p>
        <p>
          I have not been the bottleneck on his own website since it launched, which is the outcome
          I was actually aiming for.
        </p>
      </section>

      {/* 10 -------------------------------------------------------------- */}
      <section className='cs-section'>
        <p className='cs-num'>10</p>
        <h2>Honestly</h2>
        <p>
          I overdelivered here deliberately. He is a genuinely good coach in a crowded market and
          the brief he wrote would not have helped him, so I built the thing that would. That was my
          choice and I would make it again.
        </p>
        <p>
          What I would change is the sequencing. Brand and design ran well ahead of any code, which
          is defensible, but it meant the first thing he could actually click arrived late. Now I
          would ship a small real site early and let the research keep improving it, so the client
          has something working in the market while the deeper work continues.
        </p>
        <p>
          The site is live, he updates it himself, and enquiries reach him with the details already
          filled in.
        </p>
      </section>

      <footer className='cs-footer'>
        <a className='cs-cta' href={SITE} target='_blank' rel='noreferrer noopener'>
          Visit powerbyjs.com
          <span className='sr-only'>, opens in a new tab</span>
        </a>
        <a className='cs-cta cs-cta--ghost' href='/#contact'>
          Talk to me about your project
        </a>
      </footer>
    </main>
  </>
)

export default PowerByJS
